import axios from "axios";
import Config from "../utilities/Config";
const BASE_URL = `${Config.baseUrl}api/admin`;

class ClientRequests {
  getHeaders() {
    const token = localStorage.getItem("txtconnect_ng_admin_token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Fetch users with server-side pagination and filters.
   * @param {Object} params - Query params: page, limit, search, sort_order, date_from, date_to, status,
   *   sms_inactive_days, subscription_inactive_days (used with status "inactive")
   */
  async getAllClients(params = {}) {
    try {
      const query = new URLSearchParams();
      if (params.page != null) query.set("page", params.page);
      if (params.limit != null) query.set("limit", params.limit);
      if (params.search != null && params.search !== "") query.set("search", params.search);
      if (params.sort_order != null) query.set("sort_order", params.sort_order);
      if (params.date_from != null && params.date_from !== "") query.set("date_from", params.date_from);
      if (params.date_to != null && params.date_to !== "") query.set("date_to", params.date_to);
      if (params.status != null && params.status !== "") query.set("status", params.status);
      if (params.sms_inactive_days != null && params.sms_inactive_days !== "")
        query.set("sms_inactive_days", params.sms_inactive_days);
      if (params.subscription_inactive_days != null && params.subscription_inactive_days !== "")
        query.set("subscription_inactive_days", params.subscription_inactive_days);

      const url = `${BASE_URL}/all_users${query.toString() ? `?${query.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: this.getHeaders(),
      });
      return {
        status: true,
        data: response.data,
        message: "Clients fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message: error?.response?.data?.reason || "Error fetching clients data",
      };
    }
  }

  /**
   * Export users matching the same filters as getAllClients (no page/limit — all matches)
   * and trigger a browser download.
   * @param {Object} params - search, sort_order, date_from, date_to, status,
   *   sms_inactive_days, subscription_inactive_days
   */
  async exportUsers(params = {}) {
    try {
      const query = new URLSearchParams();
      if (params.search != null && params.search !== "") query.set("search", params.search);
      if (params.sort_order != null) query.set("sort_order", params.sort_order);
      if (params.date_from != null && params.date_from !== "") query.set("date_from", params.date_from);
      if (params.date_to != null && params.date_to !== "") query.set("date_to", params.date_to);
      if (params.status != null && params.status !== "") query.set("status", params.status);
      if (params.sms_inactive_days != null && params.sms_inactive_days !== "")
        query.set("sms_inactive_days", params.sms_inactive_days);
      if (params.subscription_inactive_days != null && params.subscription_inactive_days !== "")
        query.set("subscription_inactive_days", params.subscription_inactive_days);

      const url = `${BASE_URL}/export_users${query.toString() ? `?${query.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: this.getHeaders(),
        responseType: "blob",
      });

      // Prefer the server's filename; Content-Disposition may be hidden cross-origin,
      // so fall back on Content-Type to pick the extension
      const contentType =
        response.data?.type || response.headers["content-type"] || "text/csv";
      let fileName =
        contentType.includes("spreadsheet") || contentType.includes("excel")
          ? "users_export.xlsx"
          : "users_export.csv";
      const contentDisposition = response.headers["content-disposition"];
      const fileNameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (fileNameMatch) fileName = decodeURIComponent(fileNameMatch[1]);

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: contentType })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      return {
        status: true,
        message: "Users exported successfully",
      };
    } catch (error) {
      // With responseType "blob", error bodies arrive as a Blob — read the JSON reason out of it
      let reason = null;
      const errorData = error?.response?.data;
      if (errorData instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorData.text());
          reason = parsed?.data?.reason || parsed?.reason || parsed?.message || null;
        } catch {
          reason = null;
        }
      }
      return {
        status: false,
        message: reason || "Error exporting users",
      };
    }
  }

  async updateUserStatus(userSlug, status) {
    try {
      const response = await axios.post(
        `${BASE_URL}/update/user_status`,
        {
          user_slug: userSlug,
          status: status,
        },
        {
          headers: this.getHeaders(),
        }
      );
      return {
        status: true,
        data: response.data,
        message: "User status updated successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message: error?.response?.data?.reason || "Error updating user status",
      };
    }
  }

  async verifyUser(userSlug) {
    try {
      const response = await axios.post(
        `${BASE_URL}/verify_user/${userSlug}`,
        {},
        {
          headers: this.getHeaders(),
        }
      );
      return {
        status: true,
        data: response.data,
        message: "User verified successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message: error?.response?.data?.reason || "Error verifying user",
      };
    }
  }

  async deleteUser(userSlug) {
    try {
      const response = await axios.post(
        `${BASE_URL}/destroy_user`,
        {
          user_slug: userSlug,
        },
        {
          headers: this.getHeaders(),
        }
      );
      return {
        status: true,
        data: response.data,
        message: "User deleted successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message: error?.response?.data?.reason || "Error deleting user",
      };
    }
  }

  async showUserDetails(userSlug) {
    try {
      const response = await axios.get(`${BASE_URL}/show_user/${userSlug}`, {
        headers: this.getHeaders(),
      });
      return {
        status: true,
        data: response.data.data,
        message:
          response.data.data.reason || "User details fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error?.response?.data?.data?.reason || "Error showing user details",
      };
    }
  }

  async searchUsers(searchQuery) {
    try {
      const response = await axios.get(
        `${BASE_URL}/search_user?q=${encodeURIComponent(searchQuery)}`,
        {
          headers: this.getHeaders(),
        }
      );
      return {
        status: true,
        data: response.data,
        message: "Users searched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error?.response?.data?.data?.reason ||
          error?.response?.data?.reason ||
          "Error searching users",
      };
    }
  }
}
const clientRequests = new ClientRequests();
export default clientRequests;
